import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { apiClient } from '@/shared/api/api.client'

export interface UseFilteredQueryOptions<T> {
	endpoint: string
	queryKey: string
	defaultFilters?: Record<string, string>
	transform?: (data: unknown) => T[]
}

export function useFilteredQuery<T>({
	endpoint,
	queryKey,
	defaultFilters = {},
	transform,
}: UseFilteredQueryOptions<T>) {
	const [tempFilters, setTempFilters] = useState<Record<string, string>>({ ...defaultFilters })
	const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({ ...defaultFilters })

	const applyFilters = () => {
		setAppliedFilters({ ...tempFilters })
	}

	const clearFilters = () => {
		setTempFilters({ ...defaultFilters })
		setAppliedFilters({ ...defaultFilters })
	}

	const query = useQuery({
		queryKey: [queryKey, appliedFilters],
		queryFn: async () => {
			const params = new URLSearchParams()
			for (const [key, value] of Object.entries(appliedFilters)) {
				if (value && value.trim() !== '') {
					params.append(`filters[${key}]`, value)
				}
			}
			const qs = params.toString()
			const url = qs ? `${endpoint}?${qs}` : endpoint
			const response = await apiClient.get<T[] | { data: T[]; total: number }>(url)
			if (transform) return transform(response.data)
			const body = response.data
			if (Array.isArray(body)) return body
			if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
				return (body as { data: T[] }).data
			}
			return []
		},
	})

	return {
		data: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error,
		tempFilters,
		setTempFilters,
		appliedFilters,
		applyFilters,
		clearFilters,
	}
}
